/* global chrome */
/**
 * Network Scanner
 *
 * Chrome Extension-powered tool for discovering SOLUM gateways on the
 * client's local network and pushing firmware updates directly.
 *
 * Two discovery modes:
 *   1. Network Probe  – scan a subnet (e.g. 192.168.1.*)
 *   2. Server Mode    – pull gateway list from AIMS Plus dashboard
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import './styles/NetworkScanner.css';

// ── Extension Communication ─────────────────────────────────────

// The extension is signed with a fixed key (manifest "key"), so its ID is stable
// on every machine. The server reports it via /extension-info; a manual override
// in localStorage is only used if that lookup fails.
let knownExtensionId = '';

function getExtensionId() {
  return knownExtensionId || localStorage.getItem('solum-ext-id') || '';
}

function sendToExtension(message) {
  return new Promise((resolve, reject) => {
    const extId = getExtensionId();
    if (!extId) {
      reject(new Error('Extension ID not configured. Install the SOLUM Gateway Tools extension and enter its ID.'));
      return;
    }
    if (!chrome?.runtime?.sendMessage) {
      reject(new Error('chrome.runtime.sendMessage not available. Are you using Chrome?'));
      return;
    }
    try {
      chrome.runtime.sendMessage(extId, message, (response) => {
        if (chrome.runtime.lastError) {
          reject(new Error(chrome.runtime.lastError.message));
        } else if (!response) {
          reject(new Error('No response from extension. Is it installed and enabled?'));
        } else {
          resolve(response);
        }
      });
    } catch (err) {
      reject(new Error(`Failed to contact extension: ${err.message}`));
    }
  });
}

/**
 * Run a subnet/IP scan over a long-lived port so progress streams live.
 * Resolves with the full gateway list; calls onProgress({checked,total,found,current})
 * for every batch the extension reports.
 */
function scanViaPort(lines, timeoutMs, onProgress) {
  return new Promise((resolve, reject) => {
    const extId = getExtensionId();
    if (!extId) {
      reject(new Error('Extension ID not configured. Install the SOLUM Gateway Tools extension and enter its ID.'));
      return;
    }
    if (!chrome?.runtime?.connect) {
      reject(new Error('chrome.runtime.connect not available. Are you using Chrome?'));
      return;
    }

    let port;
    try {
      port = chrome.runtime.connect(extId, { name: 'scan' });
    } catch (err) {
      reject(new Error(`Failed to connect to extension: ${err.message}`));
      return;
    }

    let settled = false;
    port.onMessage.addListener((msg) => {
      if (!msg) return;
      if (msg.type === 'progress') {
        onProgress(msg);
      } else if (msg.type === 'done') {
        settled = true;
        resolve(msg.gateways || []);
        try { port.disconnect(); } catch { /* noop */ }
      } else if (msg.type === 'error') {
        settled = true;
        reject(new Error(msg.error || 'Scan error'));
        try { port.disconnect(); } catch { /* noop */ }
      }
    });
    port.onDisconnect.addListener(() => {
      if (!settled) {
        const le = chrome.runtime.lastError;
        reject(new Error(le ? le.message : 'Extension disconnected before the scan finished.'));
      }
    });

    port.postMessage({ action: 'scan', lines, timeoutMs });
  });
}


// ── Shared target-gateway list with per-action result status ────

function TargetList({ selected, results }) {
  return (
    <div className="ns-fw-targets">
      <h3>Target Gateways ({selected.length})</h3>
      {selected.length === 0 ? (
        <p className="ns-empty">No gateways selected. Go to the Gateway Discovery tab and select gateways first.</p>
      ) : (
        <div className="ns-fw-target-list">
          {selected.map(gw => {
            const result = results.find(r => r.ip === gw.ipAddress);
            return (
              <div key={gw.ipAddress} className={`ns-fw-target ${result ? (result.success ? 'success' : 'failed') : ''}`}>
                <span className="ns-mono">{gw.ipAddress}</span>
                <span className="ns-mono ns-dim">{gw.macAddress}</span>
                <span>{gw.currentfwVer}</span>
                {result && (
                  <span className={`ns-status ${result.success ? 'green' : 'red'}`}>
                    {result.success ? 'OK' : 'Failed'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ── Component ───────────────────────────────────────────────────

const NetworkScanner = () => {
  // Extension state
  const [extensionId, setExtensionId] = useState(() => getExtensionId());
  const [extensionStatus, setExtensionStatus] = useState('checking'); // checking | connected | error
  const [extensionVersion, setExtensionVersion] = useState('');
  const [extInfo, setExtInfo] = useState(null); // { id, version, forcelistEntry, ... } from the server
  const [downloading, setDownloading] = useState('');  // which download is in flight
  const extAvailableVersion = extInfo?.version || '';

  // Discovery mode
  const [discoveryMode, setDiscoveryMode] = useState('probe'); // probe | server

  // Probe mode state
  const [subnetInput, setSubnetInput] = useState('');
  const [probeTimeout, setProbeTimeout] = useState(500);

  // Server mode state
  const [serverProtocol, setServerProtocol] = useState('https');
  const [serverAddress, setServerAddress] = useState('');
  const [serverPort, setServerPort] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [stores, setStores] = useState({});
  const [selectedStores, setSelectedStores] = useState([]);

  // Gateway results
  const [gateways, setGateways] = useState([]);
  const [selectedGateways, setSelectedGateways] = useState(new Set());

  // Scan state
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(null); // { checked, total, found }

  // Firmware
  const [firmwareFile, setFirmwareFile] = useState(null);
  const [firmwareVersion, setFirmwareVersion] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);

  // Certificates (rabbitmqCrt) + RabbitMQ config (rabbitmqConfig)
  const [caCertFile, setCaCertFile] = useState(null);
  const [clientCertFile, setClientCertFile] = useState(null);
  const [clientKeyFile, setClientKeyFile] = useState(null);
  const [certIpTimeServer, setCertIpTimeServer] = useState('');
  const [certUploading, setCertUploading] = useState(false);
  const [certResults, setCertResults] = useState([]);
  const [mq, setMq] = useState({ ipRabbitMqUrl: '', mqVhost: '', mqPort: '', mqId: '', mqPass: '' });
  const [mqPushing, setMqPushing] = useState(false);
  const [mqResults, setMqResults] = useState([]);

  // Debug tab (hidden until SECRETDEBUG) — SSH enable/disable
  const [debugUnlocked, setDebugUnlocked] = useState(false);
  const [sshBusy, setSshBusy] = useState(false);
  const [sshResults, setSshResults] = useState([]);

  // Admin tab (hidden until SECRETADMIN) — run-policy flags (default ON, as in v3.4.0)
  const [adminUnlocked, setAdminUnlocked] = useState(false);
  const [enforceHopWait, setEnforceHopWait] = useState(true);
  const [enforceUploadCooling, setEnforceUploadCooling] = useState(true);

  // Tabs
  const [activeTab, setActiveTab] = useState('scan'); // scan | firmware | certificates | debug | admin

  // Log
  const [logs, setLogs] = useState([]);
  const logRef = useRef(null);

  const addLog = useCallback((msg, level = 'info') => {
    const entry = { time: new Date().toLocaleTimeString(), msg, level };
    setLogs(prev => [...prev.slice(-200), entry]);
  }, []);

  // Auto-scroll logs
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs]);

  // ── Extension health check ──

  const checkExtension = useCallback(async () => {
    try {
      const resp = await sendToExtension({ action: 'ping' });
      if (resp.ok) {
        setExtensionStatus('connected');
        setExtensionVersion(resp.version);
        addLog(`Extension connected (v${resp.version})`);
      } else {
        setExtensionStatus('error');
      }
    } catch {
      setExtensionStatus('error');
    }
  }, [addLog]);

  useEffect(() => {
    if (extensionId) {
      checkExtension();
    } else {
      setExtensionStatus('error');
    }
  }, [extensionId, checkExtension]);

  // Ask the server for the extension's stable ID + version, then connect to it
  // automatically — no ID pasting.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/network-scanner/extension-info')
      .then(r => (r.ok ? r.json() : null))
      .then(info => {
        if (cancelled || !info?.id) return;
        knownExtensionId = info.id;
        setExtInfo(info);
        setExtensionId(info.id);
      })
      .catch(() => { /* route not deployed yet — fall back to a manual ID */ });
    return () => { cancelled = true; };
  }, []);

  // Until connected, keep re-checking so the page flips to "connected" on its
  // own as soon as Chrome finishes force-installing the extension.
  useEffect(() => {
    if (extensionStatus === 'connected' || !extensionId) return undefined;
    const timer = setInterval(checkExtension, 5000);
    return () => clearInterval(timer);
  }, [extensionStatus, extensionId, checkExtension]);

  const saveExtensionId = (id) => {
    setExtensionId(id);
    localStorage.setItem('solum-ext-id', id);
  };

  // Download a server-generated file (installer, zip) and log the outcome.
  const downloadFile = async (url, filename, label, doneMsg) => {
    setDownloading(label);
    addLog(`Downloading ${label}...`);
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      addLog(doneMsg, 'success');
    } catch (err) {
      addLog(`${label} download failed: ${err.message}`, 'error');
    } finally {
      setDownloading('');
    }
  };

  const downloadInstaller = () => downloadFile(
    '/api/network-scanner/install-policy.cmd',
    'install-solum-gateway-tools.cmd',
    'installer',
    'Installer downloaded. Run it, approve the admin prompt, then restart Chrome — the extension installs itself.'
  );

  const downloadDevZip = () => downloadFile(
    '/api/network-scanner/extension.zip',
    `solum-gateway-tools${extAvailableVersion ? `-v${extAvailableVersion}` : ''}.zip`,
    'extension zip',
    'Zip downloaded. Unzip it, then "Load unpacked" in chrome://extensions (Developer mode).'
  );

  const copyForcelistEntry = async () => {
    try {
      await navigator.clipboard.writeText(extInfo.forcelistEntry);
      addLog('Policy value copied to clipboard', 'success');
    } catch {
      addLog('Could not copy — select the value and copy it manually', 'warn');
    }
  };

  // ── Network Probe Scan ──

  const runProbeScan = async () => {
    if (!subnetInput.trim()) {
      addLog('Enter at least one subnet or IP', 'error');
      return;
    }

    const lines = subnetInput.split('\n').map(l => l.trim()).filter(Boolean);
    const total = lines.reduce((sum, l) => sum + (l.endsWith('.*') ? 254 : 1), 0);

    setScanning(true);
    setGateways([]);
    setSelectedGateways(new Set());
    setScanProgress({ checked: 0, total, found: 0, current: '' });
    addLog(`Scanning ${lines.length} target(s) — ${total} address(es)...`);

    try {
      const found = await scanViaPort(lines, probeTimeout, (p) => setScanProgress(p));
      setGateways(found);
      addLog(
        `Scan complete — ${found.length} gateway(s) found`,
        found.length > 0 ? 'success' : 'warn'
      );
    } catch (err) {
      addLog(`Scan failed: ${err.message}`, 'error');
    } finally {
      setScanProgress(null);
      setScanning(false);
    }
  };

  // ── Server Mode ──

  const fetchStores = async () => {
    if (!serverAddress.trim()) {
      addLog('Enter a server address', 'error');
      return;
    }

    setScanning(true);
    addLog(`Fetching stores from ${serverAddress}...`);

    try {
      const resp = await sendToExtension({
        action: 'getStores',
        protocol: serverProtocol,
        address: serverAddress,
        port: serverPort,
        apiKey,
      });

      if (resp.ok) {
        setStores(resp.stores);
        const count = Object.keys(resp.stores).length;
        addLog(`Found ${count} store(s)`, 'success');
      } else {
        addLog(`Failed to fetch stores: ${resp.error}`, 'error');
      }
    } catch (err) {
      addLog(`Store fetch error: ${err.message}`, 'error');
    }

    setScanning(false);
  };

  const fetchGatewaysForStores = async () => {
    if (selectedStores.length === 0) {
      addLog('Select at least one store', 'error');
      return;
    }

    setScanning(true);
    setGateways([]);
    setSelectedGateways(new Set());
    const allGateways = [];

    for (const store of selectedStores) {
      addLog(`Fetching gateways for store ${store}...`);
      try {
        const resp = await sendToExtension({
          action: 'getGatewaysForStore',
          protocol: serverProtocol,
          address: serverAddress,
          port: serverPort,
          store,
          apiKey,
        });

        if (resp.ok) {
          // Normalize gateway data from AIMS Plus format
          const normalized = resp.gateways.map(gw => ({
            ipAddress: gw.ipAddress || gw.ip || gw.accessPointIp || 'N/A',
            macAddress: gw.macAddress || gw.mac || 'N/A',
            name: gw.name || gw.accessPointName || gw.setupMemo || 'N/A',
            currentfwVer: gw.currentfwVer || gw.version || gw.fwVersion || 'Unknown',
            typeName: gw.typeName || gw.type || 'Unknown',
            store: String(store),
            serverIp: serverAddress,
            siteCode: gw.siteCode || 'N/A',
            networkStatus: gw.networkStatus || gw.status || 'Unknown',
          }));
          allGateways.push(...normalized);
          addLog(`Store ${store}: ${normalized.length} gateway(s)`, 'success');
        } else {
          addLog(`Failed for store ${store}: ${resp.error}`, 'error');
        }
      } catch (err) {
        addLog(`Error for store ${store}: ${err.message}`, 'error');
      }
    }

    setGateways(allGateways);
    setScanning(false);
    addLog(`Total: ${allGateways.length} gateway(s)`, 'success');
  };

  // ── Firmware Upload ──

  const handleFirmwareSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFirmwareFile(file);

    // Try to extract version from filename
    const match = file.name.match(/N?(\d+\.\d+\.\d+\.\d+)/);
    if (match) {
      setFirmwareVersion(match[0]);
      addLog(`Firmware selected: ${file.name} (version: ${match[0]})`);
    } else {
      addLog(`Firmware selected: ${file.name} — enter version manually`);
    }
  };

  const pushFirmware = async () => {
    if (!firmwareFile || !firmwareVersion) {
      addLog('Select a firmware file and enter the version', 'error');
      return;
    }

    const targetGateways = gateways.filter(gw => selectedGateways.has(gw.ipAddress));
    if (targetGateways.length === 0) {
      addLog('Select at least one gateway to update', 'error');
      return;
    }

    setUploading(true);
    setUploadResults([]);
    addLog(`Starting firmware push to ${targetGateways.length} gateway(s)...`);

    // Read firmware file as base64
    const reader = new FileReader();
    const base64 = await new Promise((resolve) => {
      reader.onload = () => {
        const result = reader.result.split(',')[1]; // strip data: prefix
        resolve(result);
      };
      reader.readAsDataURL(firmwareFile);
    });

    const results = [];

    for (const gw of targetGateways) {
      addLog(`Uploading to ${gw.ipAddress} (${gw.macAddress})...`);
      try {
        const resp = await sendToExtension({
          action: 'uploadFirmware',
          ip: gw.ipAddress,
          firmwareBase64: base64,
          version: firmwareVersion,
        });

        const success = resp.ok && resp.success;
        results.push({ ip: gw.ipAddress, mac: gw.macAddress, success, error: resp.error });
        addLog(
          `${gw.ipAddress}: ${success ? 'Firmware accepted' : `Upload failed${resp.error ? ` — ${resp.error}` : ''}`}`,
          success ? 'success' : 'error'
        );
      } catch (err) {
        results.push({ ip: gw.ipAddress, mac: gw.macAddress, success: false, error: err.message });
        addLog(`${gw.ipAddress}: Error — ${err.message}`, 'error');
      }
    }

    setUploadResults(results);
    setUploading(false);

    const successCount = results.filter(r => r.success).length;
    addLog(`Firmware push complete: ${successCount}/${results.length} succeeded`, successCount === results.length ? 'success' : 'warn');
  };

  // ── Secret unlock (type SECRETDEBUG / SECRETADMIN into the subnet box) ──
  // Mirrors the v3.4.0 suite: plain substring match, strip the code from the
  // field, reveal the hidden tab. Unlock lasts for the session.
  const handleSubnetChange = (value) => {
    let text = value;
    if (text.includes('SECRETDEBUG')) {
      text = text.replace(/SECRETDEBUG/g, '');
      if (!debugUnlocked) {
        setDebugUnlocked(true);
        addLog('Debug tab unlocked', 'success');
      }
    }
    if (text.includes('SECRETADMIN')) {
      text = text.replace(/SECRETADMIN/g, '');
      if (!adminUnlocked) {
        setAdminUnlocked(true);
        addLog('Admin tab unlocked', 'success');
      }
    }
    setSubnetInput(text);
  };

  // ── File → base64 (strip the data: URI prefix) ──
  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });

  const targetGateways = () => gateways.filter(gw => selectedGateways.has(gw.ipAddress));

  // ── Certificate push (rabbitmqCrt) ──
  const pushCertificates = async () => {
    if (!caCertFile || !clientCertFile || !clientKeyFile) {
      addLog('Select the CA cert, client cert, and client key files', 'error');
      return;
    }
    const targets = targetGateways();
    if (targets.length === 0) {
      addLog('Select at least one gateway (Gateway Discovery tab)', 'error');
      return;
    }

    setCertUploading(true);
    setCertResults([]);
    addLog(`Pushing certificates to ${targets.length} gateway(s)...`);

    let caCertB64, clientCertB64, clientKeyB64;
    try {
      [caCertB64, clientCertB64, clientKeyB64] = await Promise.all([
        fileToBase64(caCertFile), fileToBase64(clientCertFile), fileToBase64(clientKeyFile),
      ]);
    } catch (err) {
      addLog(err.message, 'error');
      setCertUploading(false);
      return;
    }

    const results = [];
    for (const gw of targets) {
      addLog(`Certificates → ${gw.ipAddress}...`);
      try {
        const resp = await sendToExtension({
          action: 'uploadCertificates',
          ip: gw.ipAddress,
          caCertB64, clientCertB64, clientKeyB64,
          ipTimeServer: certIpTimeServer,
        });
        const success = resp.ok && resp.success;
        results.push({ ip: gw.ipAddress, success, error: resp.error });
        addLog(`${gw.ipAddress}: ${success ? 'Certificates accepted' : `Failed${resp.error ? ` — ${resp.error}` : ''}`}`, success ? 'success' : 'error');
      } catch (err) {
        results.push({ ip: gw.ipAddress, success: false, error: err.message });
        addLog(`${gw.ipAddress}: Error — ${err.message}`, 'error');
      }
    }
    setCertResults(results);
    setCertUploading(false);
    const ok = results.filter(r => r.success).length;
    addLog(`Certificate push complete: ${ok}/${results.length} succeeded`, ok === results.length ? 'success' : 'warn');
  };

  // ── RabbitMQ config push (rabbitmqConfig) ──
  const pushMqConfig = async () => {
    const { ipRabbitMqUrl, mqVhost, mqPort, mqId, mqPass } = mq;
    if (!ipRabbitMqUrl || !mqVhost || !mqPort || !mqId || !mqPass) {
      addLog('All RabbitMQ fields are required', 'error');
      return;
    }
    if (!/^\d+$/.test(String(mqPort))) {
      addLog('RabbitMQ port must be numeric', 'error');
      return;
    }
    const targets = targetGateways();
    if (targets.length === 0) {
      addLog('Select at least one gateway (Gateway Discovery tab)', 'error');
      return;
    }

    setMqPushing(true);
    setMqResults([]);
    addLog(`Pushing RabbitMQ config to ${targets.length} gateway(s)...`);

    const results = [];
    for (const gw of targets) {
      addLog(`RabbitMQ config → ${gw.ipAddress}...`);
      try {
        const resp = await sendToExtension({
          action: 'pushRabbitMQConfig',
          ip: gw.ipAddress,
          ipRabbitMqUrl, mqVhost, mqPort, mqId, mqPass,
        });
        const success = resp.ok && resp.success;
        results.push({ ip: gw.ipAddress, success, error: resp.error });
        addLog(`${gw.ipAddress}: ${success ? 'RabbitMQ config accepted' : `Failed${resp.error ? ` — ${resp.error}` : ''}`}`, success ? 'success' : 'error');
      } catch (err) {
        results.push({ ip: gw.ipAddress, success: false, error: err.message });
        addLog(`${gw.ipAddress}: Error — ${err.message}`, 'error');
      }
    }
    setMqResults(results);
    setMqPushing(false);
    const ok = results.filter(r => r.success).length;
    addLog(`RabbitMQ config complete: ${ok}/${results.length} succeeded`, ok === results.length ? 'success' : 'warn');
  };

  // ── Debug: SSH enable/disable (/api/v2/gw/sshd) ──
  const toggleSsh = async (enable) => {
    const targets = targetGateways();
    if (targets.length === 0) {
      addLog('Select at least one gateway (Gateway Discovery tab)', 'error');
      return;
    }
    setSshBusy(true);
    setSshResults([]);
    addLog(`${enable ? 'Enabling' : 'Disabling'} SSH on ${targets.length} gateway(s)...`);

    const results = [];
    for (const gw of targets) {
      try {
        const resp = await sendToExtension({ action: 'setSshd', ip: gw.ipAddress, enable });
        const success = resp.ok && resp.success;
        results.push({ ip: gw.ipAddress, enable, success, error: resp.error });
        addLog(`${gw.ipAddress}: SSH ${enable ? 'enable' : 'disable'} ${success ? 'OK' : `failed${resp.error ? ` — ${resp.error}` : ''}`}`, success ? 'success' : 'error');
      } catch (err) {
        results.push({ ip: gw.ipAddress, enable, success: false, error: err.message });
        addLog(`${gw.ipAddress}: Error — ${err.message}`, 'error');
      }
    }
    setSshResults(results);
    setSshBusy(false);
  };

  // ── Gateway selection helpers ──

  const toggleGateway = (ip) => {
    setSelectedGateways(prev => {
      const next = new Set(prev);
      if (next.has(ip)) next.delete(ip);
      else next.add(ip);
      return next;
    });
  };

  const selectAllGateways = () => {
    if (selectedGateways.size === gateways.length) {
      setSelectedGateways(new Set());
    } else {
      setSelectedGateways(new Set(gateways.map(gw => gw.ipAddress)));
    }
  };

  // ── Store selection helpers ──

  const toggleStore = (code) => {
    setSelectedStores(prev =>
      prev.includes(code) ? prev.filter(s => s !== code) : [...prev, code]
    );
  };

  // ── Render ────────────────────────────────────────────────────

  return (
    <div className="ns-container">
      {/* Header */}
      <div className="ns-header">
        <div className="ns-header-top">
          <div>
            <h1>Network Scanner</h1>
            <p>Discover SOLUM gateways and push firmware updates via Chrome Extension</p>
          </div>
          <div className="ns-ext-status">
            <span className={`ns-dot ${extensionStatus === 'connected' ? 'green' : 'red'}`} />
            {extensionStatus === 'connected'
              ? `Extension v${extensionVersion}`
              : 'Extension not connected'}
          </div>
        </div>
      </div>

      {/* Extension install (shown until the extension answers) */}
      {extensionStatus !== 'connected' && (
        <div className="ns-setup-banner">
          <strong>Install SOLUM Gateway Tools</strong>
          {extAvailableVersion ? ` (v${extAvailableVersion})` : ''} — Chrome installs it automatically
          and keeps it updated. This page connects on its own once it's in.

          <div className="ns-install-row">
            <button
              onClick={downloadInstaller}
              disabled={downloading === 'installer'}
              className="ns-btn ns-btn-primary ns-btn-download"
            >
              {downloading === 'installer' ? 'Downloading…' : '⬇ Download Installer'}
            </button>
            <span className="ns-step-text">
              Run it → approve the admin prompt → restart Chrome. Done.
              <span className="ns-dim"> (SmartScreen? More info → Run anyway.)</span>
            </span>
          </div>

          {extInfo && !extInfo.cbcmTokenConfigured && (
            <p className="ns-note ns-note-warn ns-install-note">
              Chrome only auto-installs a self-hosted extension on a <strong>managed</strong> PC
              (domain-joined, Entra-joined, or enrolled in Chrome Browser Cloud Management). On an
              unmanaged PC the policy is ignored until it's enrolled.
            </p>
          )}

          <details className="ns-details">
            <summary>Advanced — GPO / Intune, manual install</summary>
            {extInfo && (
              <div className="ns-details-block">
                <label>ExtensionInstallForcelist value (GPO / Intune / Google Admin)</label>
                <div className="ns-setup-row">
                  <input type="text" readOnly value={extInfo.forcelistEntry}
                    className="ns-input ns-mono" style={{ flex: 1 }}
                    onFocus={e => e.target.select()} />
                  <button onClick={copyForcelistEntry} className="ns-btn">Copy</button>
                </div>
              </div>
            )}
            <div className="ns-details-block">
              <label>Manual (Developer mode, no policy)</label>
              <div className="ns-setup-row">
                <button onClick={downloadDevZip} disabled={downloading === 'extension zip'} className="ns-btn">
                  {downloading === 'extension zip' ? 'Downloading…' : 'Download unpacked zip'}
                </button>
                <span className="ns-step-text ns-dim">
                  chrome://extensions → Developer mode → Load unpacked. Same extension ID, auto-detected.
                </span>
              </div>
            </div>
            {!extInfo && (
              <div className="ns-details-block">
                <label>Extension ID (only needed if the server lookup failed)</label>
                <div className="ns-setup-row">
                  <input type="text" placeholder="Chrome Extension ID" value={extensionId}
                    onChange={e => saveExtensionId(e.target.value)}
                    className="ns-input" style={{ flex: 1 }} />
                  <button onClick={checkExtension} className="ns-btn ns-btn-primary">Connect</button>
                </div>
              </div>
            )}
          </details>
        </div>
      )}

      {/* Tabs */}
      <div className="ns-tabs">
        <button className={activeTab === 'scan' ? 'active' : ''} onClick={() => setActiveTab('scan')}>
          Gateway Discovery
        </button>
        <button className={activeTab === 'firmware' ? 'active' : ''} onClick={() => setActiveTab('firmware')}>
          Firmware Update
          {selectedGateways.size > 0 && <span className="ns-badge">{selectedGateways.size}</span>}
        </button>
        <button className={activeTab === 'certificates' ? 'active' : ''} onClick={() => setActiveTab('certificates')}>
          Certificates
        </button>
        {debugUnlocked && (
          <button className={activeTab === 'debug' ? 'active' : ''} onClick={() => setActiveTab('debug')}>
            Debug
          </button>
        )}
        {adminUnlocked && (
          <button className={activeTab === 'admin' ? 'active' : ''} onClick={() => setActiveTab('admin')}>
            Admin
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="ns-content">
        {activeTab === 'scan' && (
          <div className="ns-scan-tab">
            {/* Discovery Mode Toggle */}
            <div className="ns-mode-toggle">
              <button
                className={discoveryMode === 'probe' ? 'active' : ''}
                onClick={() => setDiscoveryMode('probe')}
              >
                Network Probe
              </button>
              <button
                className={discoveryMode === 'server' ? 'active' : ''}
                onClick={() => setDiscoveryMode('server')}
              >
                AIMS Plus Server
              </button>
            </div>

            {/* Probe Mode */}
            {discoveryMode === 'probe' && (
              <div className="ns-probe-config">
                <div className="ns-field-group">
                  <label>Subnet / IP addresses (one per line)</label>
                  <textarea
                    value={subnetInput}
                    onChange={e => handleSubnetChange(e.target.value)}
                    placeholder={"192.168.1.*\n10.0.0.*\n172.16.0.100"}
                    rows={4}
                    className="ns-textarea"
                  />
                </div>
                <div className="ns-field-row">
                  <div className="ns-field-group" style={{ maxWidth: 180 }}>
                    <label>Probe timeout (ms)</label>
                    <input
                      type="number"
                      value={probeTimeout}
                      onChange={e => setProbeTimeout(Number(e.target.value))}
                      className="ns-input"
                      min={100}
                      max={10000}
                      step={100}
                    />
                  </div>
                  <button
                    onClick={runProbeScan}
                    disabled={scanning || extensionStatus !== 'connected'}
                    className="ns-btn ns-btn-primary ns-btn-lg"
                  >
                    {scanning ? 'Scanning...' : 'Scan Now'}
                  </button>
                </div>
              </div>
            )}

            {/* Server Mode */}
            {discoveryMode === 'server' && (
              <div className="ns-server-config">
                <div className="ns-field-row">
                  <div className="ns-field-group" style={{ maxWidth: 110 }}>
                    <label>Protocol</label>
                    <select value={serverProtocol} onChange={e => setServerProtocol(e.target.value)} className="ns-select">
                      <option value="https">HTTPS</option>
                      <option value="http">HTTP</option>
                    </select>
                  </div>
                  <div className="ns-field-group" style={{ flex: 1 }}>
                    <label>Server Address</label>
                    <input
                      type="text"
                      value={serverAddress}
                      onChange={e => setServerAddress(e.target.value)}
                      placeholder="192.168.1.100"
                      className="ns-input"
                    />
                  </div>
                  <div className="ns-field-group" style={{ maxWidth: 100 }}>
                    <label>Port</label>
                    <input
                      type="text"
                      value={serverPort}
                      onChange={e => setServerPort(e.target.value)}
                      placeholder="(optional)"
                      className="ns-input"
                    />
                  </div>
                </div>
                <div className="ns-field-row">
                  <div className="ns-field-group" style={{ flex: 1 }}>
                    <label>API Key / Token</label>
                    <input
                      type="password"
                      value={apiKey}
                      onChange={e => setApiKey(e.target.value)}
                      placeholder="Bearer token or API key"
                      className="ns-input"
                    />
                  </div>
                  <button
                    onClick={fetchStores}
                    disabled={scanning || extensionStatus !== 'connected'}
                    className="ns-btn ns-btn-primary"
                  >
                    {scanning ? 'Loading...' : 'Get Stores'}
                  </button>
                </div>

                {/* Store list */}
                {Object.keys(stores).length > 0 && (
                  <div className="ns-store-list">
                    <label>Select stores to scan ({Object.keys(stores).length} found)</label>
                    <div className="ns-store-grid">
                      {Object.entries(stores).map(([code, data]) => (
                        <label key={code} className={`ns-store-item ${selectedStores.includes(code) ? 'selected' : ''}`}>
                          <input
                            type="checkbox"
                            checked={selectedStores.includes(code)}
                            onChange={() => toggleStore(code)}
                          />
                          <span>{code}</span>
                          {data.name && <span className="ns-store-name">{data.name}</span>}
                        </label>
                      ))}
                    </div>
                    <button
                      onClick={fetchGatewaysForStores}
                      disabled={scanning || selectedStores.length === 0 || extensionStatus !== 'connected'}
                      className="ns-btn ns-btn-primary ns-btn-lg"
                      style={{ marginTop: 8 }}
                    >
                      {scanning ? 'Fetching...' : `Get Gateways (${selectedStores.length} store${selectedStores.length !== 1 ? 's' : ''})`}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Scan Progress */}
            {scanProgress && (
              <div className="ns-progress-bar">
                <div className="ns-progress-info">
                  <span>Scanning {scanProgress.current || ''}...</span>
                  <span>{scanProgress.checked}/{scanProgress.total} checked — {scanProgress.found} found</span>
                </div>
                <div className="ns-progress-track">
                  <div
                    className="ns-progress-fill"
                    style={{ width: `${(scanProgress.checked / scanProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Gateway Results Table */}
            {gateways.length > 0 && (
              <div className="ns-results">
                <div className="ns-results-header">
                  <h3>Gateways Found ({gateways.length})</h3>
                  <div className="ns-results-actions">
                    <button onClick={selectAllGateways} className="ns-btn ns-btn-sm">
                      {selectedGateways.size === gateways.length ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedGateways.size > 0 && (
                      <button onClick={() => setActiveTab('firmware')} className="ns-btn ns-btn-primary ns-btn-sm">
                        Push Firmware ({selectedGateways.size})
                      </button>
                    )}
                  </div>
                </div>
                <div className="ns-table-wrap">
                  <table className="ns-table">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>
                          <input
                            type="checkbox"
                            checked={selectedGateways.size === gateways.length && gateways.length > 0}
                            onChange={selectAllGateways}
                          />
                        </th>
                        <th>IP Address</th>
                        <th>MAC Address</th>
                        <th>Name</th>
                        <th>Firmware</th>
                        <th>Type</th>
                        <th>Store</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gateways.map((gw) => (
                        <tr
                          key={gw.ipAddress}
                          className={selectedGateways.has(gw.ipAddress) ? 'selected' : ''}
                          onClick={() => toggleGateway(gw.ipAddress)}
                        >
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedGateways.has(gw.ipAddress)}
                              onChange={() => toggleGateway(gw.ipAddress)}
                              onClick={e => e.stopPropagation()}
                            />
                          </td>
                          <td className="ns-mono">{gw.ipAddress}</td>
                          <td className="ns-mono">{gw.macAddress}</td>
                          <td>{gw.name}</td>
                          <td className="ns-mono">{gw.currentfwVer}</td>
                          <td>{gw.typeName}</td>
                          <td>{gw.store}</td>
                          <td>
                            <span className={`ns-status ${gw.networkStatus === 'Connected' ? 'green' : 'gray'}`}>
                              {gw.networkStatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'firmware' && (
          <div className="ns-firmware-tab">
            <div className="ns-fw-config">
              <div className="ns-field-row">
                <div className="ns-field-group" style={{ flex: 1 }}>
                  <label>Firmware File (.tar.gz)</label>
                  <input
                    type="file"
                    accept=".tar.gz,.gz,.tar"
                    onChange={handleFirmwareSelect}
                    className="ns-input"
                  />
                </div>
                <div className="ns-field-group" style={{ maxWidth: 200 }}>
                  <label>Version</label>
                  <input
                    type="text"
                    value={firmwareVersion}
                    onChange={e => setFirmwareVersion(e.target.value)}
                    placeholder="e.g. N1.3.6.0"
                    className="ns-input"
                  />
                </div>
              </div>

              {firmwareFile && (
                <div className="ns-fw-info">
                  <span>File: {firmwareFile.name}</span>
                  <span>Size: {(firmwareFile.size / 1048576).toFixed(1)} MB</span>
                  <span>Version: {firmwareVersion || '(not set)'}</span>
                </div>
              )}

              <div className="ns-fw-targets">
                <h3>Target Gateways ({selectedGateways.size})</h3>
                {selectedGateways.size === 0 ? (
                  <p className="ns-empty">No gateways selected. Go to Gateway Discovery tab and select gateways first.</p>
                ) : (
                  <div className="ns-fw-target-list">
                    {gateways.filter(gw => selectedGateways.has(gw.ipAddress)).map(gw => {
                      const result = uploadResults.find(r => r.ip === gw.ipAddress);
                      return (
                        <div key={gw.ipAddress} className={`ns-fw-target ${result ? (result.success ? 'success' : 'failed') : ''}`}>
                          <span className="ns-mono">{gw.ipAddress}</span>
                          <span className="ns-mono ns-dim">{gw.macAddress}</span>
                          <span>{gw.currentfwVer}</span>
                          {result && (
                            <span className={`ns-status ${result.success ? 'green' : 'red'}`}>
                              {result.success ? 'OK' : 'Failed'}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <button
                onClick={pushFirmware}
                disabled={uploading || selectedGateways.size === 0 || !firmwareFile || !firmwareVersion || extensionStatus !== 'connected'}
                className="ns-btn ns-btn-primary ns-btn-lg ns-btn-push"
              >
                {uploading ? 'Pushing Firmware...' : `Push Firmware to ${selectedGateways.size} Gateway(s)`}
              </button>
            </div>
          </div>
        )}

        {activeTab === 'certificates' && (
          <div className="ns-firmware-tab">
            <div className="ns-fw-config">
              <p className="ns-note ns-note-warn">
                These endpoints are HTTPS with the gateway's self-signed certificate. A browser
                cannot bypass an untrusted cert, so if a push fails with <code>Failed to fetch</code>,
                open <code>https://&lt;gateway-ip&gt;</code> in a tab once, accept the certificate,
                then retry. The extension also tries plain HTTP automatically.
              </p>

              {/* ── RabbitMQ certificates (rabbitmqCrt) ── */}
              <div className="ns-section">
                <h3>RabbitMQ Certificates</h3>
                <div className="ns-field-row">
                  <div className="ns-field-group" style={{ flex: 1 }}>
                    <label>CA Certificate (PEM)</label>
                    <input type="file" accept=".pem,.crt,.cer,.ca" className="ns-input"
                      onChange={e => setCaCertFile(e.target.files[0] || null)} />
                  </div>
                  <div className="ns-field-group" style={{ flex: 1 }}>
                    <label>Client Certificate (PEM)</label>
                    <input type="file" accept=".pem,.crt,.cer" className="ns-input"
                      onChange={e => setClientCertFile(e.target.files[0] || null)} />
                  </div>
                </div>
                <div className="ns-field-row">
                  <div className="ns-field-group" style={{ flex: 1 }}>
                    <label>Client Key (PEM)</label>
                    <input type="file" accept=".pem,.key" className="ns-input"
                      onChange={e => setClientKeyFile(e.target.files[0] || null)} />
                  </div>
                  <div className="ns-field-group" style={{ flex: 1 }}>
                    <label>Time Server IP (optional)</label>
                    <input type="text" value={certIpTimeServer} placeholder="e.g. 10.0.0.1"
                      className="ns-input" onChange={e => setCertIpTimeServer(e.target.value)} />
                  </div>
                </div>
                <button
                  onClick={pushCertificates}
                  disabled={certUploading || selectedGateways.size === 0 || !caCertFile || !clientCertFile || !clientKeyFile || extensionStatus !== 'connected'}
                  className="ns-btn ns-btn-primary ns-btn-lg"
                >
                  {certUploading ? 'Pushing Certificates...' : `Push Certificates to ${selectedGateways.size} Gateway(s)`}
                </button>
              </div>

              {/* ── RabbitMQ config (rabbitmqConfig) ── */}
              <div className="ns-section">
                <h3>RabbitMQ Config</h3>
                <div className="ns-field-row">
                  <div className="ns-field-group" style={{ flex: 1 }}>
                    <label>Server URL</label>
                    <input type="text" value={mq.ipRabbitMqUrl} placeholder="broker host / IP"
                      className="ns-input" onChange={e => setMq({ ...mq, ipRabbitMqUrl: e.target.value })} />
                  </div>
                  <div className="ns-field-group" style={{ maxWidth: 140 }}>
                    <label>vHost</label>
                    <input type="text" value={mq.mqVhost} placeholder="/"
                      className="ns-input" onChange={e => setMq({ ...mq, mqVhost: e.target.value })} />
                  </div>
                  <div className="ns-field-group" style={{ maxWidth: 110 }}>
                    <label>Port</label>
                    <input type="text" value={mq.mqPort} placeholder="5671"
                      className="ns-input" onChange={e => setMq({ ...mq, mqPort: e.target.value })} />
                  </div>
                </div>
                <div className="ns-field-row">
                  <div className="ns-field-group" style={{ flex: 1 }}>
                    <label>Username</label>
                    <input type="text" value={mq.mqId} className="ns-input"
                      onChange={e => setMq({ ...mq, mqId: e.target.value })} />
                  </div>
                  <div className="ns-field-group" style={{ flex: 1 }}>
                    <label>Password</label>
                    <input type="password" value={mq.mqPass} className="ns-input"
                      onChange={e => setMq({ ...mq, mqPass: e.target.value })} />
                  </div>
                </div>
                <button
                  onClick={pushMqConfig}
                  disabled={mqPushing || selectedGateways.size === 0 || extensionStatus !== 'connected'}
                  className="ns-btn ns-btn-primary ns-btn-lg"
                >
                  {mqPushing ? 'Pushing Config...' : `Push RabbitMQ Config to ${selectedGateways.size} Gateway(s)`}
                </button>
              </div>

              <TargetList
                selected={gateways.filter(gw => selectedGateways.has(gw.ipAddress))}
                results={[...certResults, ...mqResults]}
              />
            </div>
          </div>
        )}

        {activeTab === 'debug' && debugUnlocked && (
          <div className="ns-firmware-tab">
            <div className="ns-fw-config">
              <div className="ns-section">
                <h3>SSH Control <span className="ns-mono ns-dim">/api/v2/gw/sshd</span></h3>
                <p className="ns-note">
                  Enable or disable SSH on the selected gateways. This is the only Debug action a
                  browser can perform — interactive shell, DPS check, SaaS diagnostic, tcpdump/pcap
                  and live-log capture are SSH/SFTP operations that require the desktop
                  SOLUM&nbsp;Gateway&nbsp;Suite v3.4.0.
                </p>
                <div className="ns-field-row">
                  <button onClick={() => toggleSsh(true)}
                    disabled={sshBusy || selectedGateways.size === 0 || extensionStatus !== 'connected'}
                    className="ns-btn ns-btn-primary ns-btn-lg">
                    {sshBusy ? 'Working...' : `Enable SSH (${selectedGateways.size})`}
                  </button>
                  <button onClick={() => toggleSsh(false)}
                    disabled={sshBusy || selectedGateways.size === 0 || extensionStatus !== 'connected'}
                    className="ns-btn ns-btn-lg">
                    {sshBusy ? 'Working...' : `Disable SSH (${selectedGateways.size})`}
                  </button>
                </div>
              </div>

              <TargetList
                selected={gateways.filter(gw => selectedGateways.has(gw.ipAddress))}
                results={sshResults}
              />
            </div>
          </div>
        )}

        {activeTab === 'admin' && adminUnlocked && (
          <div className="ns-firmware-tab">
            <div className="ns-fw-config">
              <div className="ns-section">
                <h3>Run Policy</h3>
                <label className="ns-checkbox-row">
                  <input type="checkbox" checked={enforceHopWait}
                    onChange={e => setEnforceHopWait(e.target.checked)} />
                  <span>Enforce the 5-minute hop wait (between multi-step firmware hops)</span>
                </label>
                <label className="ns-checkbox-row">
                  <input type="checkbox" checked={enforceUploadCooling}
                    onChange={e => setEnforceUploadCooling(e.target.checked)} />
                  <span>Enforce 5-min upload-retry cooling (gw fw ≥ 1.3.3.x)</span>
                </label>
              </div>
              <div className="ns-section">
                <h3>USB Bridge</h3>
                <p className="ns-note">
                  Install / Uninstall USB Bridge (Newton Gen5 L2 bridge) is an SSH operation and is
                  only available in the desktop SOLUM&nbsp;Gateway&nbsp;Suite v3.4.0 — it cannot run
                  from a browser.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Log Panel */}
      <div className="ns-log-panel">
        <div className="ns-log-header">
          <span>Activity Log</span>
          <button onClick={() => setLogs([])} className="ns-btn ns-btn-sm">Clear</button>
        </div>
        <div className="ns-log-entries" ref={logRef}>
          {logs.map((entry, i) => (
            <div key={i} className={`ns-log-entry ${entry.level}`}>
              <span className="ns-log-time">{entry.time}</span>
              <span className="ns-log-msg">{entry.msg}</span>
            </div>
          ))}
          {logs.length === 0 && (
            <div className="ns-log-empty">No activity yet</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NetworkScanner;
